<!-- @format -->
<script context="module" lang="ts">
  export type CarouselConfig = {
    initalSlideTo?: number;
    aspectRatio?: number;
    count?: number;
    pos?: number
  }
</script>

<script lang="ts">
  import Swiper from "swiper";
  import type { SwiperOptions } from "swiper/types";
  import { Navigation } from "swiper/modules";
  import { onMount, tick } from "svelte";
  import { generateRandomID } from "~/util/common";
  import { lazyLoadImage } from "~/util/actions";

  export let config: CarouselConfig;
  export let isStorePage = false;
  const { count = 1, initalSlideTo, aspectRatio = 1 } = config;

  const id = generateRandomID();
  let isInitialized = false;
  let slideWidth = 0;
  let reachEnd = false;
  let reachBegining = false;
  let swiper: Swiper | undefined;
  let swiperEl: HTMLElement;

  const params: SwiperOptions = {
    modules: [Navigation],
    navigation: {
      enabled: true,
      nextEl: `#next-${id}`,
      prevEl: `#prev-${id}`,
    },
    on: { 
      slideChange, 
      resize, 
      init: (swiper) => {
        resize(swiper);
        isInitialized = true;
      }
    },
    watchSlidesProgress: true,
    slidesPerView: 1.3,
    centeredSlides: true,
    loop: true,
    spaceBetween: 0,
    initialSlide: initalSlideTo,
    breakpoints: {
      768: {
        slidesPerView: Math.min(count, 4),
        centeredSlides: count > 4,
      },
      992: {
        slidesPerView: Math.min(count, 5),
        centeredSlides: count > 5,
      },
      1200: {
        slidesPerView: Math.min(count, 7),
        centeredSlides: count > 7,
      },
    },
  };

  onMount(async () => {
    swiper = new Swiper(swiperEl, {
      ...params,
      modules: [Navigation],
      wrapperClass: 'swiper-wrapper',
      slideClass: 'swiper-slide'
    });

    // Wait a tick to ensure DOM is ready
    await tick();
    swiperEl.classList.add('swiper-initialized');
    isInitialized = true;
  });

  function resize(e: Swiper) {
    const slides = Array.from(e.slides);
    const visibleSlides = slides.filter(slide => slide.classList.contains('swiper-slide-visible'));
    if (visibleSlides.length > 0) {
      slideWidth = visibleSlides[0].clientWidth;
    }
    slideChange(e);
  }

  const opacity = "0.12";

  function slideChange(e: Swiper) {
    const slides = Array.from(e.slides);
    const visibleSlides = slides.filter(slide => slide.classList.contains('swiper-slide-visible'));
    const [first, ...rest] = visibleSlides;
    const width = e.width ?? 0;
    const activeIndex = e.activeIndex;
    
    visibleSlides.forEach((el: HTMLElement) => {
      el.style.opacity = "1";
      el.style.pointerEvents = "auto";
      const img = el.querySelector<HTMLImageElement>("img.lazy-loading-src");
      if (!img || img.src) return;
      const src = img.getAttribute("data-src");
      if (src && img) lazyLoadImage(img, src);
    });

    if (!first || !rest.length) return;

    if (width >= 1200) {
      reachEnd = activeIndex == count - 7;
      reachBegining = activeIndex == 6;
      if (count < 7) return;
    } else if (width >= 992) {
      reachEnd = activeIndex == count - 5;
      reachBegining = activeIndex == 4;
      if (count < 5) return;
    } else if (width >= 744) {
      reachEnd = activeIndex == count - 4;
      reachBegining = activeIndex == 3;
      if (count < 4) return;
    } else {
      reachEnd = activeIndex == count - 1;
      reachBegining = activeIndex == 0;
    }

    if (reachEnd) {
      first.style.opacity = opacity;
      first.style.pointerEvents = "none";
      return;
    }
    if (reachBegining) {
      const lastSlide = rest.at(-1);
      if (lastSlide) {
        lastSlide.style.opacity = opacity;
        lastSlide.style.pointerEvents = "none";
      }
      return;
    }
    first.style.opacity = opacity;
    first.style.pointerEvents = "none";
    const lastSlide = rest.at(-1);
    if (lastSlide) {
      lastSlide.style.opacity = opacity;
      lastSlide.style.pointerEvents = "none";
    }
  }
</script>

<div class="lift-carousel-container" style="--aspect-ratio: {aspectRatio}">
  <div class="lift-carousel">
    <button
      class="button-prev"
      class:d-none={reachBegining}
      class:d-md-none={count < 4 || reachBegining}
      class:d-lg-none={count < 6 || reachBegining}
      id="prev-{id}"
      aria-label="Previous slide">
      <i class="bi bi-chevron-left shadow" />
    </button>

    <div 
      class="swiper"
      class:store-page={isStorePage}
      class:visibility-hidden={!isInitialized}
      bind:this={swiperEl}>
      <div class="swiper-wrapper">
        <slot name="swiper" />
      </div>
    </div>

    <button
      class="button-next"
      class:d-none={reachEnd}
      class:d-md-none={count < 4 || reachEnd}
      class:d-lg-none={count < 6 || reachEnd}
      id="next-{id}"
      aria-label="Next slide">
      <i class="bi bi-chevron-right shadow" />
    </button>
  </div>
</div>

<style lang="scss">
  .lift-carousel-container {
    --slide-width: 216px;
    --max-slide-width: 320px;
    --navigation-width: calc(var(--slide-width) / 2 - 8px);
    --navigation-height: calc((var(--slide-width) - 16px) * var(--aspect-ratio));
    padding: 0 24px;
    position: relative;

    :global(.swiper) {
      overflow-y: visible;
      position: relative;
      width: 100%;
      
      &.store-page {
        max-width: calc(var(--max-slide-width) * 4);
        margin: 0 auto;

        :global(.swiper-slide) {
          max-width: var(--max-slide-width);
        }
      }
    }

    :global(.swiper-wrapper) {
      display: flex;
      width: 100%;
      height: auto;
      position: relative;
    }

    :global(.swiper-slide) {
      flex-shrink: 0;
      width: var(--slide-width);
      height: auto;
      position: relative;
      transition: opacity 0.3s ease, pointer-events 0s;
    }
  }

  .lift-carousel {
    position: relative;
    margin: 0 -12px;
    min-height: 216px;
  }

  .visibility-hidden {
    visibility: hidden;
  }

  :global(.swiper-initialized) {
    visibility: visible;
    animation: fadeIn 0.3s ease forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .button-next,
  .button-prev {
    position: absolute;
    top: 33%;
    transform: translateY(-50%);
    z-index: 10;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-accent-primary);
    min-width: 30px;
    width: var(--navigation-width);
    border: none;
    background: none;
    padding: 0;

    @media screen and (max-width: 767px) {
      width: 13% !important;
    }

    i {
      background: none;
      transform: none;
      width: auto;
      height: auto;
    }
  }

  .button-prev .bi-chevron-left:before,
  .button-next .bi-chevron-right:before {
    background-color: #fff;
    border-radius: 50%;
    width: 38px;
    height: 38px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    @media screen and (min-width: 576px) {
      width: 40px;
      height: 40px;
      font-size: 26px;
    }
  }

  .button-prev {
    left: 12px;
  }

  .button-next {
    right: 12px;
  }

  i.shadow {
    border-radius: 50%;
  }
</style>
